// Row validation: a loaded client set or page must fit its source, carry unique non-empty row identities,
// give every ineligible row a reason and put every row in a declared group, before selection, expansion or
// state publication uses it.

const isText = value => typeof value === 'string' && value.trim() !== ''
const isObject = value => Boolean(value) && typeof value === 'object'

const checkSourceSize = (config, rows, response, errors) => {
  const source = isObject(config.source) ? config.source : {}
  if (source.mode === 'client' && rows.length > source.max) {
    errors.push(`rows: ${rows.length} rows exceed source.max (${source.max})`)
  }
  if (source.mode !== 'page') return
  const { total, pageSize } = response
  if (total !== undefined) {
    if (!Number.isInteger(total) || total < rows.length)
      errors.push(
        'total: a page-backed response supplies the exact matching count'
      )
  }
  if (Number.isInteger(pageSize) && rows.length > pageSize)
    errors.push(
      `rows: ${rows.length} rows exceed the active page size (${pageSize})`
    )
}

const reasonFor = (selection, row) => {
  const reason = selection.ineligibleReason
  return typeof reason === 'function' ? reason(row) : reason
}

const groupKeys = grouped =>
  new Set(
    (Array.isArray(grouped.groups) ? grouped.groups : [])
      .filter(isObject)
      .map(group => group.key)
  )

const groupOf = (grouped, row) =>
  typeof grouped.groupOf === 'function'
    ? grouped.groupOf(row)
    : row[grouped.key]

/**
 * Every problem in one loaded client set or page. `response` carries a page-backed response's `total` and
 * the active `pageSize`, when the caller has them.
 * @returns {string[]}
 */
export const validateRows = (config, rows, response = {}) => {
  if (!Array.isArray(rows)) return ['rows: the source must supply an array']
  const cfg = isObject(config) ? config : {}
  const errors = []
  checkSourceSize(cfg, rows, response || {}, errors)

  const selection =
    isObject(cfg.selection) && typeof cfg.selection.isEligible === 'function'
      ? cfg.selection
      : null
  const grouped = isObject(cfg.grouped) ? cfg.grouped : null
  const groups = grouped ? groupKeys(grouped) : null

  const seen = new Set()
  rows.forEach((row, i) => {
    const id = isObject(row) ? row[cfg.rowKey] : undefined
    if (id === undefined || id === null || id === '') {
      errors.push(`rows[${i}]: empty row identity`)
      return
    }
    if (seen.has(String(id))) {
      errors.push(`rows[${i}]: duplicate row identity ${id}`)
      return
    }
    seen.add(String(id))
    if (selection && selection.isEligible(row) === false) {
      if (!isText(reasonFor(selection, row)))
        errors.push(`rows[${i}]: ineligible row ${id} has no ineligible reason`)
    }
    if (groups && !groups.has(groupOf(grouped, row)))
      errors.push(`rows[${i}]: row ${id} belongs to no declared group`)
  })
  return errors
}
